import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowsClockwise, AirplaneTilt, TrendDown, TrendUp, BellRinging, MapTrifold } from "@phosphor-icons/react";
import api from "../lib/api";
import RouteCard from "../components/RouteCard";
import UpdatedAgo from "../components/UpdatedAgo";
import AnimatedCounter from "../components/AnimatedCounter";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const STATS = [
  { key: "routes", label: "Active Routes", icon: MapTrifold, color: "var(--saa-blue)" },
  { key: "saa_cheapest", label: "SAA Cheapest", icon: TrendDown, color: "var(--success)" },
  { key: "saa_undercut", label: "SAA Undercut", icon: TrendUp, color: "var(--danger)" },
  { key: "alerts", label: "Active Alerts", icon: BellRinging, color: "var(--warning)" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [refreshing, setRefreshing] = useState(false);
  const [integrity, setIntegrity] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, i] = await Promise.all([
        api.get(`/dashboard/summary?months=${months}`),
        api.get("/data-sources/integrity").catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setIntegrity(i.data);
    } catch (e) {
      console.error(e);
    }
  }, [months]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const triggerRefresh = async (useSerpapi = false, useTier1 = false) => {
    setRefreshing(true);
    try {
      await api.post(`/data-sources/refresh?use_serpapi=${useSerpapi}&use_tier1=${useTier1}`);
      toast.success(
        useTier1 ? "Full live refresh started — Tier 1 + SerpAPI" :
        useSerpapi ? "Live refresh started — SerpAPI" :
        "Refresh started in background"
      );
      let tries = 0;
      const id = setInterval(async () => {
        await load();
        if (++tries > 20) clearInterval(id);
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const filteredRoutes = data?.routes || [];
  const undercut = filteredRoutes.filter((r) => r.status === "undercut" && r.gap_pct > 15);

  return (
    <div className="p-6 lg:p-10 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]"
               style={{ color: "var(--saa-gold)" }}>
            <AirplaneTilt size={14} weight="duotone" />
            Commercial Operations
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2"
              data-testid="dashboard-title">
            Fare Intelligence
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
            Live competitor monitoring across 24 strategic routes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <UpdatedAgo iso={data?.stats?.last_refresh} onClick={load} />
          <Select value={`${months}`} onValueChange={(v) => setMonths(parseInt(v))}>
            <SelectTrigger data-testid="months-select" className="w-[140px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 months</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="9">9 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="refresh-mock-btn"
            variant="outline"
            onClick={() => triggerRefresh(false)}
            disabled={refreshing}
            className="h-9"
          >
            <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin mr-1" : "mr-1"} />
            Refresh
          </Button>
          <Button
            data-testid="refresh-live-btn"
            onClick={() => triggerRefresh(true)}
            disabled={refreshing}
            className="h-9 font-semibold"
            style={{ background: "var(--saa-gold)", color: "#0A1428" }}
          >
            Live fetch (SerpAPI)
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {STATS.map((s, i) => {
          const Icon = s.icon;
          const v = data?.stats?.[s.key] ?? 0;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="saa-card p-5"
              data-testid={`stat-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                  {s.label}
                </div>
                <Icon size={18} weight="duotone" color={s.color} />
              </div>
              <div className="font-display text-3xl font-bold">
                <AnimatedCounter value={v} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Data integrity banner */}
      {integrity && integrity.unverified_cells > 0 && (
        <div
          className="mb-6 p-4 rounded-xl flex items-start gap-3"
          style={{
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.30)",
          }}
          data-testid="integrity-banner"
        >
          <div className="text-sm flex-1" style={{ color: "var(--text-secondary)" }}>
            <span className="font-semibold" style={{ color: "var(--warning)" }}>
              {integrity.unverified_cells} unverified cell{integrity.unverified_cells > 1 ? "s" : ""}
            </span>{" "}
            present ({integrity.verified_pct}% of {integrity.total_cells} are verified).
            Stats above and route averages exclude unverified cells. Click <span className="font-mono">Refresh</span> to fetch live data.
          </div>
        </div>
      )}
      {integrity && integrity.total_cells === 0 && (
        <div
          className="mb-6 p-4 rounded-xl flex items-start gap-3"
          style={{
            background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.30)",
          }}
          data-testid="empty-banner"
        >
          <div className="text-sm flex-1" style={{ color: "var(--text-secondary)" }}>
            <span className="font-semibold" style={{ color: "var(--info, #3B82F6)" }}>
              No fare data yet.
            </span>{" "}
            Data Integrity Mode is ON — the app refuses to fabricate fares. Click <span className="font-mono">Refresh</span> above to pull verified data from Duffel NDC + Google Flights.
          </div>
        </div>
      )}

      {/* Alert banner */}
      {undercut.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-xl flex items-start gap-3"
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.30)",
          }}
          data-testid="alert-banner"
        >
          <BellRinging size={20} weight="duotone" color="#EF4444" className="mt-0.5" />
          <div>
            <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
              {undercut.length} route{undercut.length > 1 ? "s" : ""} undercut by &gt;15%
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              {undercut.slice(0, 6).map((r) => `${r.origin}-${r.destination}`).join(", ")}
              {undercut.length > 6 && "…"}
            </div>
          </div>
        </motion.div>
      )}

      {/* Route grid */}
      {!data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="saa-card p-5 h-[260px] animate-pulse">
              <div className="h-4 w-1/2 mb-3" style={{ background: "var(--border-subtle)" }} />
              <div className="h-3 w-3/4" style={{ background: "var(--border-subtle)" }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
             data-testid="route-grid">
          {filteredRoutes.map((r, i) => (
            <RouteCard key={r.route_id} route={r} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
